import { useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router";
import {
  ArrowLeft,
  Calendar,
  Clock,
  Users,
  Coins,
  CheckCircle,
  AlertCircle,
  GitBranch,
  FileText,
  Send,
  ChevronRight,
  X,
} from "lucide-react";
import { useAuthStore } from "@/shared/hooks/use-auth-store";
import {
  useBountyDetailV2,
  useDeliveryReviews,
  useSubmitBidV2,
  useEndBiddingSelectWinner,
  useSubmitDeliveryReview,
  useReviewDelivery,
  formatReward,
  formatDate,
  formatDateTime,
  getDaysRemaining,
} from "@/lib/hooks/use-bounties-v2";
import { useBidsForBounty, useMyBid } from "@/lib/hooks/use-bounties";
import { getStatusLabel, getStatusColor, MAX_REVIEW_ROUNDS } from "@/domains/bounties/status-machine";
import type { BountyStatus } from "@/lib/supabase/types";
import { BidModal } from "./bid-modal";
import styles from "./task-detail-page.module.css";

// ========== Phase Components ==========

/** 投递期阶段展示 */
function BiddingPhase({
  bounty,
  bidCount,
  hasBid,
  onBidClick,
  isPublisher,
}: {
  bounty: { id: string; title: string; reward_usdc: number; bidding_deadline: string | null };
  bidCount: number;
  hasBid: boolean;
  onBidClick: () => void;
  isPublisher: boolean;
}) {
  const daysLeft = getDaysRemaining(bounty.bidding_deadline);

  return (
    <div className={styles.phaseCard}>
      <div className={styles.phaseHeader}>
        <div className={styles.phaseIcon} style={{ background: "rgba(99, 102, 241, 0.15)" }}>
          <Users size={24} color="#6366F1" />
        </div>
        <div className={styles.phaseInfo}>
          <h3 className={styles.phaseTitle}>投递期</h3>
          <p className={styles.phaseDesc}>
            {daysLeft !== null && daysLeft > 0
              ? `距离投递截止还有 ${daysLeft} 天`
              : "投递已截止"}
          </p>
        </div>
      </div>

      <div className={styles.phaseStats}>
        <div className={styles.phaseStat}>
          <span className={styles.statValue}>{bidCount}</span>
          <span className={styles.statLabel}>已投递方案</span>
        </div>
        <div className={styles.phaseStat}>
          <span className={styles.statValue}>{formatDate(bounty.bidding_deadline)}</span>
          <span className={styles.statLabel}>投递截止</span>
        </div>
      </div>

      {!isPublisher && (
        <button
          className={`${styles.phaseActionBtn} ${hasBid ? styles.disabled : ""}`}
          onClick={onBidClick}
          disabled={hasBid || daysLeft === null || daysLeft <= 0}
        >
          {hasBid ? "已投递方案" : daysLeft === null || daysLeft <= 0 ? "投递已截止" : "投递方案"}
        </button>
      )}

      {isPublisher && (
        <div className={styles.phaseHint}>
          <AlertCircle size={14} />
          您可以在投递截止后选择中标方案
        </div>
      )}
    </div>
  );
}

/** 开发期阶段展示 */
function DevelopmentPhase({
  bounty,
  selectedEngineer,
  isEngineer,
  onSubmitDelivery,
}: {
  bounty: { completion_deadline: string | null };
  selectedEngineer?: string | null;
  isEngineer: boolean;
  onSubmitDelivery: () => void;
}) {
  const daysLeft = getDaysRemaining(bounty.completion_deadline);

  return (
    <div className={styles.phaseCard}>
      <div className={styles.phaseHeader}>
        <div className={styles.phaseIcon} style={{ background: "rgba(245, 158, 11, 0.15)" }}>
          <Clock size={24} color="#F59E0B" />
        </div>
        <div className={styles.phaseInfo}>
          <h3 className={styles.phaseTitle}>开发期</h3>
          <p className={styles.phaseDesc}>
            {daysLeft !== null && daysLeft > 0
              ? `距离项目截止还有 ${daysLeft} 天`
              : "已逾期"}
          </p>
        </div>
      </div>

      {selectedEngineer && (
        <div className={styles.winnerInfo}>
          <span className={styles.winnerLabel}>中标者</span>
          <span className={styles.winnerName}>{selectedEngineer}</span>
        </div>
      )}

      <div className={styles.deadlineInfo}>
        <Calendar size={16} />
        <span>项目截止: {formatDate(bounty.completion_deadline)}</span>
      </div>

      {isEngineer && (
        <button className={styles.phaseActionBtn} onClick={onSubmitDelivery}>
          <Send size={16} />
          提交交付物
        </button>
      )}
    </div>
  );
}

/** 审查期阶段展示 */
function ReviewingPhase({
  bounty,
  reviews,
  isPublisher,
  isEngineer,
  onReview,
}: {
  bounty: { review_round: number };
  reviews: Array<{ id: string; round_number: number; status: string; publisher_feedback: string | null }>;
  isPublisher: boolean;
  isEngineer: boolean;
  onReview: (reviewId: string, status: "approved" | "rejected") => void;
}) {
  const currentRound = bounty.review_round;
  const latestReview = reviews[reviews.length - 1];

  return (
    <div className={styles.phaseCard}>
      <div className={styles.phaseHeader}>
        <div className={styles.phaseIcon} style={{ background: "rgba(249, 115, 22, 0.15)" }}>
          <FileText size={24} color="#F97316" />
        </div>
        <div className={styles.phaseInfo}>
          <h3 className={styles.phaseTitle}>审查期</h3>
          <p className={styles.phaseDesc}>
            第 {currentRound} / {MAX_REVIEW_ROUNDS} 轮审查
          </p>
        </div>
      </div>

      {/* 审查历史 */}
      <div className={styles.reviewHistory}>
        {reviews.map((review, index) => (
          <div
            key={review.id}
            className={`${styles.reviewItem} ${review.status === "rejected" ? styles.rejected : ""}`}
          >
            <div className={styles.reviewRound}>第{review.round_number}轮</div>
            <div className={styles.reviewStatus}>
              {review.status === "approved" ? (
                <>
                  <CheckCircle size={14} color="#10B981" />
                  已通过
                </>
              ) : review.status === "rejected" ? (
                <>
                  <AlertCircle size={14} color="#EF4444" />
                  已驳回
                </>
              ) : (
                <>
                  <Clock size={14} color="#F59E0B" />
                  待审核
                </>
              )}
            </div>
            {review.publisher_feedback && (
              <div className={styles.reviewFeedback}>{review.publisher_feedback}</div>
            )}
          </div>
        ))}
      </div>

      {/* 待审核操作 */}
      {latestReview?.status === "submitted" && isPublisher && (
        <div className={styles.reviewActions}>
          <button
            className={`${styles.reviewBtn} ${styles.rejectBtn}`}
            onClick={() => onReview(latestReview.id, "rejected")}
          >
            驳回
          </button>
          <button
            className={`${styles.reviewBtn} ${styles.approveBtn}`}
            onClick={() => onReview(latestReview.id, "approved")}
          >
            通过
          </button>
        </div>
      )}

      {latestReview?.status === "rejected" && isEngineer && currentRound < MAX_REVIEW_ROUNDS && (
        <div className={styles.phaseHint}>
          <AlertCircle size={14} />
          请根据反馈修改后重新提交
        </div>
      )}
    </div>
  );
}

/** 结算期/已完成阶段展示 */
function SettlementPhase({
  status,
  outcome,
}: {
  status: BountyStatus;
  outcome: string | null;
}) {
  const isCompleted = status === "completed";
  const outcomeText = outcome
    ? {
        expired: "投递截止未选标",
        failed_delivery: "超时未交付",
        abandoned: "中标者放弃",
        publisher_cancel: "发布者取消",
      }[outcome] || "已取消"
    : "";

  return (
    <div className={styles.phaseCard}>
      <div className={styles.phaseHeader}>
        <div
          className={styles.phaseIcon}
          style={{
            background: isCompleted ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)",
          }}
        >
          {isCompleted ? (
            <CheckCircle size={24} color="#10B981" />
          ) : (
            <AlertCircle size={24} color="#EF4444" />
          )}
        </div>
        <div className={styles.phaseInfo}>
          <h3 className={styles.phaseTitle}>
            {isCompleted ? "已完成" : "已取消"}
          </h3>
          <p className={styles.phaseDesc}>
            {isCompleted ? "任务已结算完成" : outcomeText}
          </p>
        </div>
      </div>

      {isCompleted && (
        <div className={styles.settlementInfo}>
          <div className={styles.settlementRow}>
            <span>工程师收益 (90%)</span>
            <span className={styles.settlementValue}>已发放</span>
          </div>
          <div className={styles.settlementRow}>
            <span>平台服务费 (10%)</span>
            <span className={styles.settlementValue}>已扣除</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== Modals ==========

function SubmitDeliveryModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (repoUrl: string, description: string) => void;
  isSubmitting: boolean;
}) {
  const [repoUrl, setRepoUrl] = useState("");
  const [description, setDescription] = useState("");

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>提交交付物</h3>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.formField}>
            <label>GitHub 仓库地址 *</label>
            <input
              type="url"
              placeholder="https://github.com/username/repo"
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
            />
          </div>

          <div className={styles.formField}>
            <label>提交说明 *</label>
            <textarea
              rows={4}
              placeholder="描述本次提交的内容..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>
            取消
          </button>
          <button
            className={styles.submitBtn}
            onClick={() => onSubmit(repoUrl, description)}
            disabled={!repoUrl.trim() || !description.trim() || isSubmitting}
          >
            {isSubmitting ? "提交中..." : "确认提交"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReviewModal({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting,
  action,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (feedback: string) => void;
  isSubmitting: boolean;
  action: "approved" | "rejected";
}) {
  const [feedback, setFeedback] = useState("");

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{action === "approved" ? "确认通过" : "驳回交付"}</h3>
          <button className={styles.modalClose} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {action === "rejected" && (
            <div className={styles.formField}>
              <label>驳回理由 *</label>
              <textarea
                rows={4}
                placeholder="请说明驳回原因，帮助开发者改进..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
            </div>
          )}

          {action === "approved" && (
            <p className={styles.confirmText}>确认通过此交付物？通过后任务将完成结算。</p>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>
            取消
          </button>
          <button
            className={action === "approved" ? styles.approveBtn : styles.rejectBtn}
            onClick={() => onSubmit(feedback)}
            disabled={action === "rejected" && !feedback.trim() || isSubmitting}
          >
            {isSubmitting ? "处理中..." : action === "approved" ? "确认通过" : "确认驳回"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ========== Main Component ==========

export default function TaskDetailPage() {
  const { bountyId } = useParams<{ bountyId: string }>();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Queries
  const { data: bounty, isLoading, error, refetch } = useBountyDetailV2(bountyId, user?.id ?? null);
  const { data: bids } = useBidsForBounty(bountyId);
  const { data: myBid } = useMyBid(bountyId, user?.id ?? null);
  const { data: reviews } = useDeliveryReviews(bountyId);

  // Mutations
  const submitBidMutation = useSubmitBidV2();
  const submitDeliveryMutation = useSubmitDeliveryReview();
  const reviewMutation = useReviewDelivery();

  // UI State
  const [showBidModal, setShowBidModal] = useState(false);
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [reviewAction, setReviewAction] = useState<{ reviewId: string; action: "approved" | "rejected" } | null>(null);
  const [bidError, setBidError] = useState<string | null>(null);

  // Derived state
  const isPublisher = user?.id === bounty?.publisher_id;
  const isEngineer = user?.id && bids?.some((b) => b.engineerId === user.id && b.status === "selected");
  const hasBid = !!myBid;

  // Handlers
  const handleBidSubmit = useCallback(
    async (data: { proposal: string; estimatedDays: number; contactInfo: string; proposedReward: number }) => {
      if (!bountyId) return;
      try {
        await submitBidMutation.mutateAsync({
          bountyId,
          ...data,
        });
        setShowBidModal(false);
        setBidError(null);
        refetch();
      } catch (error) {
        setBidError(error instanceof Error ? error.message : '投递失败，请重试');
      }
    },
    [bountyId, submitBidMutation, refetch]
  );

  const handleDeliverySubmit = useCallback(
    async (repoUrl: string, description: string) => {
      if (!bountyId) return;
      try {
        await submitDeliveryMutation.mutateAsync({
          bountyId,
          repoUrl,
          description,
        });
        setShowDeliveryModal(false);
        refetch();
      } catch (error) {
        // Error handled by mutation
      }
    },
    [bountyId, submitDeliveryMutation, refetch]
  );

  const handleReview = useCallback(
    async (feedback: string) => {
      if (!reviewAction) return;
      try {
        await reviewMutation.mutateAsync({
          deliveryReviewId: reviewAction.reviewId,
          status: reviewAction.action,
          feedback: feedback || undefined,
        });
        setReviewAction(null);
        refetch();
      } catch (error) {
        // Error handled by mutation
      }
    },
    [reviewAction, reviewMutation, refetch]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}>
            <ArrowLeft size={24} />
          </button>
          <h1>任务详情</h1>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.spinner} />
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !bounty) {
    return (
      <div className={styles.page}>
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={() => navigate(-1)}>
            <ArrowLeft size={24} />
          </button>
          <h1>任务详情</h1>
        </div>
        <div className={styles.errorState}>
          <p>{error?.message || "任务不存在"}</p>
          <button onClick={() => refetch()}>重试</button>
        </div>
      </div>
    );
  }

  // Render phase component based on status
  const renderPhase = () => {
    switch (bounty.status) {
      case "bidding":
        return (
          <BiddingPhase
            bounty={bounty}
            bidCount={bounty.bid_count}
            hasBid={hasBid}
            onBidClick={() => setShowBidModal(true)}
            isPublisher={isPublisher}
          />
        );

      case "in_progress":
        return (
          <DevelopmentPhase
            bounty={bounty}
            selectedEngineer={bounty.selected_engineer_nickname}
            isEngineer={!!isEngineer}
            onSubmitDelivery={() => setShowDeliveryModal(true)}
          />
        );

      case "reviewing":
        return (
          <ReviewingPhase
            bounty={bounty}
            reviews={reviews || []}
            isPublisher={isPublisher}
            isEngineer={!!isEngineer}
            onReview={(reviewId, status) => setReviewAction({ reviewId, action: status })}
          />
        );

      case "completed":
      case "cancelled":
        return <SettlementPhase status={bounty.status} outcome={bounty.outcome} />;

      default:
        return null;
    }
  };

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1>任务详情</h1>
      </div>

      {/* Hero Card */}
      <div className={styles.heroCard}>
        <div className={styles.heroContent}>
          <div className={styles.heroTags}>
            {bounty.category && <span className={styles.categoryTag}>{bounty.category}</span>}
            <span
              className={styles.statusTag}
              style={{
                color: getStatusColor(bounty.status),
                backgroundColor: `${getStatusColor(bounty.status)}20`,
              }}
            >
              {getStatusLabel(bounty.status, bounty.outcome)}
            </span>
          </div>

          <h2 className={styles.heroTitle}>{bounty.title}</h2>

          <div className={styles.heroMeta}>
            <div className={styles.heroMetaItem}>
              <Coins size={18} />
              <span>{formatReward(bounty.reward_usdc)}</span>
            </div>
            <div className={styles.heroMetaItem}>
              <Calendar size={18} />
              <span>投递截止: {formatDate(bounty.bidding_deadline)}</span>
            </div>
            <div className={styles.heroMetaItem}>
              <Calendar size={18} />
              <span>项目截止: {formatDate(bounty.completion_deadline)}</span>
            </div>
          </div>

          {bounty.publisher_nickname && (
            <div className={styles.publisherInfo}>
              <span className={styles.publisherLabel}>发布者</span>
              <span className={styles.publisherName}>{bounty.publisher_nickname}</span>
            </div>
          )}
        </div>
      </div>

      {/* Phase Section */}
      <div className={styles.section}>{renderPhase()}</div>

      {/* Description Section */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>任务描述</h3>
        <div className={styles.description}>{bounty.description || "暂无描述"}</div>
      </div>

      {/* Tech Tags */}
      {bounty.tech_tags && bounty.tech_tags.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>技术标签</h3>
          <div className={styles.techTags}>
            {bounty.tech_tags.map((tag) => (
              <span key={tag} className={styles.techTag}>
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Bids Section (Publisher only) */}
      {isPublisher && bids && bids.length > 0 && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>投递方案 ({bids.length})</h3>
          <div className={styles.bidsList}>
            {bids.map((bid) => (
              <div key={bid.id} className={styles.bidItem}>
                <div className={styles.bidHeader}>
                  <span className={styles.bidEngineer}>{bid.engineerNickname || "匿名"}</span>
                  <span className={styles.bidReward}>¥{bid.proposedReward.toLocaleString()}</span>
                </div>
                <p className={styles.bidProposal}>{bid.proposal}</p>
                {bounty.status === "bidding" && bid.status === "pending" && (
                  <button className={styles.selectBidBtn}>选择中标</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* My Bid Section */}
      {hasBid && myBid && (
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>我的方案</h3>
          <div className={styles.myBidCard}>
            <div className={styles.myBidStatus}>
              {myBid.status === "selected"
                ? "已中标"
                : myBid.status === "rejected"
                ? "未中标"
                : "待审核"}
            </div>
            <p className={styles.myBidProposal}>{myBid.proposal}</p>
            <div className={styles.myBidMeta}>
              <span>报价: ¥{myBid.proposedReward.toLocaleString()}</span>
              <span>工期: {myBid.estimatedDays} 天</span>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showBidModal && (
        <BidModal
          bountyId={bounty.id}
          bountyTitle={bounty.title}
          rewardUsdc={bounty.reward_usdc}
          onClose={() => {
            setShowBidModal(false);
            setBidError(null);
          }}
          onSubmit={handleBidSubmit}
          isSubmitting={submitBidMutation.isPending}
          error={bidError}
        />
      )}

      <SubmitDeliveryModal
        isOpen={showDeliveryModal}
        onClose={() => setShowDeliveryModal(false)}
        onSubmit={handleDeliverySubmit}
        isSubmitting={submitDeliveryMutation.isPending}
      />

      <ReviewModal
        isOpen={!!reviewAction}
        onClose={() => setReviewAction(null)}
        onSubmit={handleReview}
        isSubmitting={reviewMutation.isPending}
        action={reviewAction?.action || "approved"}
      />
    </div>
  );
}
